/**
 * PartnerListPage - Admin page listing the partner API integrations.
 *
 * Route: /admin/partners
 *
 * Shows whether each partner is actually delivering, which is not the same as
 * whether it is switched on: a partner can be enabled and still held up by a
 * missing API user, a missing adapter or an unconfigured environment. The page
 * shows that reason rather than reporting the partner as simply off.
 */

import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { useAuth } from '@/common/hooks/useAuth'
import { Stack } from '@/common/components/stack'
import { Text } from '@/common/components/text'
import { Badge } from '@/common/components/badge'
import { Icon } from '@/common/components/icon'
import { listPartners } from '@/features/partners/api/partners'
import { AdminOnly } from '@/features/partners/components/AdminOnly'
import { DeliveriesSwitch } from '@/features/partners/components/DeliveriesSwitch'

export const Route = createFileRoute({
  component: PartnerListPage,
})

export function PartnerListPage() {
  const { token, roles } = useAuth()
  const isAdmin = roles.includes('admin')

  const {
    data: partners,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['admin', 'partners'],
    queryFn: () => listPartners(token ?? ''),
    enabled: Boolean(token) && isAdmin,
  })

  if (!isAdmin) {
    return <AdminOnly title="Partners" />
  }

  return (
    <Stack gap="24" style={{ padding: 24 }}>
      <Stack gap="8">
        <Text as="h1" variant="h4" weight="bold">
          Partners
        </Text>
        <Text variant="bodySm" color="muted">
          Investor partners with an API integration. Each one receives published properties in its
          own system, with a link back to Orchard.
        </Text>
      </Stack>

      {isLoading && <Text variant="bodySm">Loading partners…</Text>}
      {error && (
        <Text variant="bodySm">
          {error instanceof Error ? error.message : 'Could not load partners.'}
        </Text>
      )}
      {partners?.length === 0 && (
        <Text variant="bodySm" color="muted">
          No partners yet.
        </Text>
      )}

      <Stack gap="16">
        {partners?.map((partner) => (
          <Stack
            key={partner.id}
            gap="8"
            style={{ padding: 16, border: '1px solid rgba(0,0,0,0.12)', borderRadius: 8 }}
          >
            <Stack direction="row" gap="12" align="center" justify="between" wrap>
              <Stack direction="row" gap="12" align="center" wrap>
                <Text variant="bodyLg" weight="bold">
                  {partner.display_name}
                </Text>
                <Badge color={partner.ready_to_deliver ? 'success' : 'neutral'}>
                  {partner.ready_to_deliver ? 'Delivering' : 'Not delivering'}
                </Badge>
                {!partner.has_adapter && <Badge color="caution">No adapter</Badge>}
              </Stack>
              <Stack direction="row" gap="16" align="center">
                <DeliveriesSwitch partner={partner} />
                <Link to="/admin/partners/$partnerId" params={{ partnerId: partner.id }}>
                  <Stack direction="row" gap="4" align="center">
                    <Text variant="bodySm" weight="bold" color="primary">
                      Manage
                    </Text>
                    <Icon name="chevron_right" size={16} />
                  </Stack>
                </Link>
              </Stack>
            </Stack>

            <Text variant="bodySm" color="muted">
              {partner.slug} · key version {partner.link_key_version} · {partner.delivered_count}{' '}
              {partner.delivered_count === 1 ? 'property' : 'properties'} delivered
            </Text>

            <Text variant="bodySm">
              {partner.api_user
                ? `Acting as ${partner.api_user.first_name} ${partner.api_user.last_name}` +
                  (partner.api_user.login_email ? ` (${partner.api_user.login_email})` : '')
                : 'No API user chosen'}
            </Text>

            {partner.blocked_reason && (
              <Text variant="bodySm" color="muted">
                {partner.blocked_reason}
              </Text>
            )}
          </Stack>
        ))}
      </Stack>
    </Stack>
  )
}
